<template>
  <div>
    <Fieldset
      :legend="toUpperCase(translate('languages', 'ucfirst'))"
      class="relative w-full px-5 pb-4 mt-5 bg-white rounded-lg shadow-md border border-color-iron"
    >
      <Skeleton v-if="isLoading && idioms" height="1.5rem" class="mb-2" />
      <ul v-else-if="!isLoading && idioms.length > 0">
        <li>
          <div
            v-for="(idiom, index) in idioms"
            :key="index"
            class="flex flex-col gap-y-3 mb-2.5 mt-5 relative"
          >
            <span class="font-medium">
              {{ idiom.language_name }} — {{ singleProficiency(idiom.speaking).title }}
            </span>
            <i
              @click="showModal('update', 'idiom', idiom)"
              class="pi pi-pencil cursor-pointer absolute top-1 -right-2"
            />
          </div>
          <i
            @click="showModal('create', 'idiom')"
            class="pi pi-plus cursor-pointer absolute -top-2 right-3 text-lg"
          />
        </li>
      </ul>
      <ul v-else>
        <li class="font-medium fixed-uppercase">
          {{ translate('no idioms', 'ucfirst') }}
          <i
            @click="showModal('create', 'idiom')"
            class="pi pi-plus cursor-pointer absolute top-1 right-5 text-lg"
          />
        </li>
      </ul>
    </Fieldset>

    <ModalDialogComponent :isvisible="visible" @close-modal="closeModalDialog">
      <template #title>
        <span class="fixed-uppercase">{{ displayModalTitle }}</span>
      </template>
      <template #content>
        <div class="fixed-uppercase-form">
          <ProfessionalLanguageForm
            :idiom-to-update="idiomToUpdate"
            @close-modal="closeModalDialog"
            @idioms-updated="updatedIdioms"
            :curriculum-id="props.idioms.curriculum.id"
          />
        </div>
      </template>
    </ModalDialogComponent>
  </div>
</template>

<script setup>
import { ref, onMounted, onUpdated } from 'vue'
import Fieldset from 'primevue/fieldset'
import Skeleton from 'primevue/skeleton'
import requestHandler from '@/composables/requestHandler'
import { translate } from '@/composables/translate'
import useModalCurriculum from '@/composables/modalDialogCurriculum'
import ModalDialogComponent from '@/components/Utils/ModalDialogComponent.vue'
import ProfessionalLanguageForm from '@/components/Dashboard/Applicant/FormsCurriculum/ProfessionalLanguageForm.vue'
import useLanguageProficiency from '@/composables/LanguageProficiency'
import { toUpperCase } from '@/composables/textUtils';

const props = defineProps({
  idioms: {
    type: Object,
    required: true,
    default: () => ({ idioms: [], curriculum: {} }),
  },
})

let idioms = ref([])
let isLoading = ref(false)
let visible = ref(false)
let idiomToUpdate = ref(0)
let { toggleCreateUpdateTitle, displayModalTitle } = useModalCurriculum()
let { singleProficiency } = useLanguageProficiency()

async function updatedIdioms() {
  isLoading.value = true
  const response = await requestHandler.executeRequest('/api/curriculum', {}, 'get')
  if (response.success) idioms.value = response.data.languages
  isLoading.value = false
}

function showModal(type, title, idiom = {}) {
  toggleCreateUpdateTitle(type, title)
  idiomToUpdate.value = idiom
  visible.value = true
}

function closeModalDialog(event) {
  visible.value = event
}

onUpdated(() => {
  isLoading.value = false
  if (props.idioms.idioms.length > 0) idioms.value = props.idioms.idioms
})

onMounted(() => {
  isLoading.value = true
})
</script>

<style scoped>
.fixed-uppercase {
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

:deep(.p-fieldset-legend-text) {
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.fixed-uppercase-form :deep(label),
.fixed-uppercase-form :deep(.p-dropdown-item),
.fixed-uppercase-form :deep(.p-radiobutton-label),
.fixed-uppercase-form :deep(.p-checkbox-label),
.fixed-uppercase-form :deep(.p-button .p-button-label),
.fixed-uppercase-form :deep(.p-dialog-title) {
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
</style>
